import { z } from 'zod';
import {
  InvoiceStatus,
  OPEN_INVOICE_STATUSES,
  PaymentMethod,
  RateBasis,
} from '../enums';
import { booleanish, dateSchema, paginationSchema, zEnum } from './common';

// ---------------------------------------------------------------------------
// Money
//
// Every figure here ends up on a tax invoice, so none of it is allowed to drift.
// All arithmetic is done in paise as integers and converted back at the edge:
// 0.1 + 0.2 is famously not 0.3, and an invoice that is one paisa out is an
// invoice the client's accounts team rejects.
// ---------------------------------------------------------------------------

/** Rupees → paise, half-up. The only place a float becomes an integer. */
export const toPaise = (rupees: number): number => Math.round(rupees * 100);

/** Paise → rupees, fixed at 2dp. */
export const toRupees = (paise: number): number => Math.round(paise) / 100;

/** Rounds a rupee figure to 2dp without going through float addition. */
export const money = (rupees: number): number => toRupees(toPaise(rupees));

/**
 * Applies a percentage to a paise amount, half-up.
 *
 * Kept separate so every percentage in the module — service charge, each GST
 * component — rounds identically. Inconsistent rounding between components is
 * how a total stops matching the sum of its parts.
 */
export const percentOfPaise = (paise: number, percent: number): number =>
  Math.round((paise * percent) / 100);

// ---------------------------------------------------------------------------
// Line computation
// ---------------------------------------------------------------------------

export interface LineRateInput {
  rateBasis: RateBasis;
  /** The agreed rate: per month for MONTHLY, per day for DAILY. */
  rate: number;
  /** Payable days from the muster — already accounts for half days. */
  payableDays: number;
  /** Calendar days in the billed month; the divisor for MONTHLY pro-rating. */
  daysInMonth: number;
  overtimeHours?: number;
  /** Per-hour overtime rate. Left to the contract rather than derived. */
  overtimeRate?: number;
  serviceChargePercent?: number;
}

export interface ComputedLine {
  wageAmount: number;
  overtimeAmount: number;
  serviceChargeAmount: number;
  lineTotal: number;
}

/**
 * Works out one worker's charge for one month.
 *
 * MONTHLY pro-rates: a person present 26 of 31 days bills 26/31 of the monthly
 * rate. This is the convention Indian manufacturing contracts use — the divisor
 * is calendar days, not working days, because weekly offs and holidays are
 * themselves payable (see ATTENDANCE_PAYABLE_FRACTION).
 *
 * The service charge is applied to wages **and** overtime, which is what
 * "service charge on the bill value" means in a standard contract.
 */
export const computeLine = (input: LineRateInput): ComputedLine => {
  const ratePaise = toPaise(input.rate);

  const wagePaise =
    input.rateBasis === RateBasis.DAILY
      ? Math.round(ratePaise * input.payableDays)
      : input.daysInMonth > 0
        ? Math.round((ratePaise * input.payableDays) / input.daysInMonth)
        : 0;

  const overtimePaise = Math.round(
    toPaise(input.overtimeRate ?? 0) * (input.overtimeHours ?? 0),
  );

  const serviceChargePaise = percentOfPaise(
    wagePaise + overtimePaise,
    input.serviceChargePercent ?? 0,
  );

  return {
    wageAmount: toRupees(wagePaise),
    overtimeAmount: toRupees(overtimePaise),
    serviceChargeAmount: toRupees(serviceChargePaise),
    lineTotal: toRupees(wagePaise + overtimePaise + serviceChargePaise),
  };
};

// ---------------------------------------------------------------------------
// GST
// ---------------------------------------------------------------------------

/**
 * Manpower supply is SAC 998519 — "other employment & labour supply services" —
 * taxed at 18%.
 */
export const MANPOWER_SAC_CODE = '998519';
export const DEFAULT_GST_RATE = 18;

export interface GstInput {
  /** Wages + overtime + service charge: the whole value of the supply. */
  taxableValue: number;
  gstRate: number;
  /** Client in a different state to the supplier → IGST instead of CGST+SGST. */
  isInterState: boolean;
}

export interface ComputedTax {
  cgst: number;
  sgst: number;
  igst: number;
  totalTax: number;
}

/**
 * Splits GST into its components.
 *
 * Intra-state supply is CGST + SGST at half the rate each; inter-state is a
 * single IGST at the full rate. The place of supply — not where the invoice is
 * raised — decides which, so the caller passes the comparison in.
 *
 * Note on what is taxed: GST applies to the **full** invoice value, wages
 * included, not to the service charge alone. Charging tax on the margin only
 * requires qualifying as a "pure agent" under Rule 33, whose conditions a
 * staffing contract almost never satisfies. This is worth confirming with your
 * CA against your actual client contracts, because it materially changes the
 * bill.
 */
export const computeTax = ({ taxableValue, gstRate, isInterState }: GstInput): ComputedTax => {
  const basePaise = toPaise(taxableValue);

  if (isInterState) {
    const igst = percentOfPaise(basePaise, gstRate);
    return { cgst: 0, sgst: 0, igst: toRupees(igst), totalTax: toRupees(igst) };
  }

  // Half each. Computed independently and summed so the two halves always add
  // back to the total shown, even when the rate is odd.
  const half = percentOfPaise(basePaise, gstRate / 2);
  return { cgst: toRupees(half), sgst: toRupees(half), igst: 0, totalTax: toRupees(half * 2) };
};

export interface InvoiceTotalsInput {
  lines: ComputedLine[];
  gstRate: number;
  isInterState: boolean;
}

export interface InvoiceTotals {
  subtotal: number;
  serviceChargeTotal: number;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  totalTax: number;
  /** The rupees-and-paise figure before rounding to a whole rupee. */
  grossTotal: number;
  /** Between -0.50 and +0.50, as printed on the invoice. */
  roundOff: number;
  /** What the client actually pays — a whole number of rupees. */
  total: number;
}

/**
 * Rolls lines into invoice totals.
 *
 * The grand total is rounded to a whole rupee and the difference shown as an
 * explicit round-off line, which is standard practice on an Indian tax invoice
 * and keeps the printed arithmetic self-consistent.
 */
export const computeInvoiceTotals = ({
  lines,
  gstRate,
  isInterState,
}: InvoiceTotalsInput): InvoiceTotals => {
  // Summed in paise; adding 200 line totals as floats is exactly how a bill
  // ends up a paisa short of the sum of its own lines.
  const subtotalPaise = lines.reduce(
    (sum, l) => sum + toPaise(l.wageAmount) + toPaise(l.overtimeAmount),
    0,
  );
  const serviceChargePaise = lines.reduce((sum, l) => sum + toPaise(l.serviceChargeAmount), 0);
  const taxablePaise = subtotalPaise + serviceChargePaise;

  const tax = computeTax({ taxableValue: toRupees(taxablePaise), gstRate, isInterState });
  const grossPaise = taxablePaise + toPaise(tax.totalTax);

  const totalPaise = Math.round(grossPaise / 100) * 100;

  return {
    subtotal: toRupees(subtotalPaise),
    serviceChargeTotal: toRupees(serviceChargePaise),
    taxableValue: toRupees(taxablePaise),
    cgst: tax.cgst,
    sgst: tax.sgst,
    igst: tax.igst,
    totalTax: tax.totalTax,
    grossTotal: toRupees(grossPaise),
    roundOff: toRupees(totalPaise - grossPaise),
    total: toRupees(totalPaise),
  };
};

// ---------------------------------------------------------------------------
// Invoice state
// ---------------------------------------------------------------------------

/** Only a draft can still be changed. */
export const isInvoiceEditable = (status: InvoiceStatus): boolean =>
  status === InvoiceStatus.DRAFT;

/** Money we are still owed. A cancelled or draft invoice is not a receivable. */
export const isReceivable = (status: InvoiceStatus): boolean =>
  OPEN_INVOICE_STATUSES.includes(status);

/**
 * The status an invoice takes after a payment lands.
 *
 * Overpayment settles rather than erroring: clients round up, pay two invoices
 * with one transfer, or clear an old short-payment along with a new bill. The
 * caller records the excess; refusing the receipt outright would be worse.
 */
export const statusAfterPayment = (total: number, paid: number): InvoiceStatus => {
  if (toPaise(paid) >= toPaise(total)) return InvoiceStatus.PAID;
  if (toPaise(paid) > 0) return InvoiceStatus.PARTIALLY_PAID;
  return InvoiceStatus.ISSUED;
};

/** Days past the due date, or 0 while it is not yet due. */
export const daysOverdue = (dueDate: Date | string, asOf: Date = new Date()): number => {
  const due = new Date(dueDate);
  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const nowDay = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate());
  return Math.max(0, Math.round((nowDay - dueDay) / 86_400_000));
};

/** The buckets a receivables ageing report is cut into. */
export const AGEING_BUCKETS = [
  { key: 'current', label: 'Not due', min: 0, max: 0 },
  { key: '1-30', label: '1–30 days', min: 1, max: 30 },
  { key: '31-60', label: '31–60 days', min: 31, max: 60 },
  { key: '61-90', label: '61–90 days', min: 61, max: 90 },
  { key: '90+', label: 'Over 90 days', min: 91, max: Infinity },
] as const;

export type AgeingBucket = (typeof AGEING_BUCKETS)[number]['key'];

export const ageingBucketFor = (overdueDays: number): AgeingBucket =>
  (AGEING_BUCKETS.find((b) => overdueDays >= b.min && overdueDays <= b.max) ?? AGEING_BUCKETS[0])
    .key;

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export const generateInvoiceSchema = z.object({
  companyId: z.string().uuid(),
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /** Defaults to today at the API. */
  invoiceDate: dateSchema.optional(),
  /** Net terms in days; the due date is derived from it. */
  paymentTermDays: z.number().int().min(0).max(180).default(30),
  gstRate: z.number().min(0).max(28).default(DEFAULT_GST_RATE),
  notes: z.string().max(2000).optional(),
});
export type GenerateInvoiceInput = z.infer<typeof generateInvoiceSchema>;

export const invoiceSearchSchema = paginationSchema.extend({
  companyId: z.string().uuid().optional(),
  status: zEnum(InvoiceStatus).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  /**
   * Issued, unpaid and past due. `booleanish`, not `z.coerce.boolean()`, which
   * turns the string "false" into `true` and inverts the filter.
   */
  overdueOnly: booleanish.optional(),
});
export type InvoiceSearchQuery = z.infer<typeof invoiceSearchSchema>;

export const issueInvoiceSchema = z.object({
  invoiceId: z.string().uuid(),
  invoiceDate: dateSchema.optional(),
  dueDate: dateSchema.optional(),
});
export type IssueInvoiceInput = z.infer<typeof issueInvoiceSchema>;

export const cancelInvoiceSchema = z.object({
  invoiceId: z.string().uuid(),
  // An issued invoice is a document the client holds; cancelling it without a
  // stated reason leaves nothing to reconcile against.
  reason: z.string().trim().min(5).max(500),
});
export type CancelInvoiceInput = z.infer<typeof cancelInvoiceSchema>;

export const recordPaymentSchema = z.object({
  invoiceId: z.string().uuid(),
  amount: z.number().positive().max(100_000_000),
  paidAt: dateSchema,
  method: zEnum(PaymentMethod).default(PaymentMethod.BANK_TRANSFER),
  reference: z.string().max(120).optional(),
  notes: z.string().max(1000).optional(),
});
export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;

export const outstandingQuerySchema = z.object({
  companyId: z.string().uuid().optional(),
  asOf: dateSchema.optional(),
});
export type OutstandingQuery = z.infer<typeof outstandingQuerySchema>;
